import Chart, { useChart } from 'src/components/chart';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import Iconify from '../../iconify';
import CustomTooltip from './CustomTooltip';
import { PresenceSegments } from '../types';

const calculateCompetitorsOnly = (data: PresenceSegments) => {
    return 100 - data.organicOnly - data.soloBidder - data.topAdPosition;
};

export default function PresenceRateChart({ data, height = 40 }: { data: PresenceSegments; height?: number }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const colors = isDark
        ? ['#18A155', '#C09910', '#F9DE81', '#545D68']
        : ['#18A155', '#C09910', '#F9DE81', '#999'];
    const chartOptions = useChart({
        chart: {
            type: 'bar',
            stacked: true,
            parentHeightOffset: 0,
            animations: { enabled: false },
            sparkline: {
                enabled: true,
            },
            toolbar: {
                show: false,
            },
            stackType: 'normal',
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '30%',
                borderRadius: 0,
            },
        },
        stroke: {
            width: 0,
            show: false,
        },
        series: [
            { name: 'Organic only', data: [data.organicOnly] },
            { name: 'Solo bidder', data: [data.soloBidder] },
            { name: 'Top ad position', data: [data.topAdPosition] },
            { name: 'Competitors only', data: [calculateCompetitorsOnly(data)] },
        ],
        colors,
        tooltip: {
            enabled: false,
            x: { show: false },
            marker: { show: false },
        },
        legend: {
            show: false,
        },
        grid: {
            show: false,
        },
        xaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            max: 100,
            min: 0,
        },
        yaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
    });

    const items = [
        { label: 'Organic only', value: Math.max(0, Math.round(data.organicOnly)), color: colors[0], icon: 'solar:global-linear' },
        { label: 'Solo bidder', value: Math.max(0, Math.round(data.soloBidder)), color: colors[1], icon: 'carbon:user' },
        { label: 'Top ad position', value: Math.max(0, Math.round(data.topAdPosition)), color: colors[2], icon: 'carbon:user-multiple' },
        { label: 'Competitors only', value: Math.max(0, Math.round(calculateCompetitorsOnly(data))), color: colors[3], icon: 'ri:sword-line' },
    ];

    return (
        <CustomTooltip
            placement="top"
            title={
                <Stack direction="column" spacing={0.75}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Presence breakdown
                    </Typography>
                    <Stack direction="column" spacing={0.5}>
                        {items.map((it) => (
                            <Stack key={it.label} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon={it.icon} sx={{ width: 16, height: 16, color: it.color }} />
                                    <Typography variant="caption">{it.label}</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{it.value}%</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            }
        >
            <Box sx={{ height, width: '100%', minWidth: 0 }}>
                <Chart
                    type="bar"
                    series={chartOptions.series}
                    options={chartOptions}
                    height={height}
                    width="100%"
                />
            </Box>
        </CustomTooltip>
    );
}


